import React from "react";
import { Typography, Paper, Box, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { withTranslation } from "react-i18next";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    const isChunkError = Boolean(
      error &&
        (error.name === "ChunkLoadError" ||
          (error.message &&
            (error.message.includes("Loading chunk") ||
              error.message.includes("missing:") ||
              error.message.includes("Failed to fetch dynamically imported module"))))
    );

    if (isChunkError) {
      const pageHasAlreadyBeenReloaded = sessionStorage.getItem("chunk_has_reloaded");
      if (!pageHasAlreadyBeenReloaded) {
        sessionStorage.setItem("chunk_has_reloaded", "true");
        window.location.reload();
        return { hasError: false, isChunkError: true };
      }
    }

    return { hasError: true, isChunkError, error };
  }

  componentDidCatch(error, errorInfo) {
    const isChunkError = Boolean(
      error &&
        (error.name === "ChunkLoadError" ||
          (error.message &&
            (error.message.includes("Loading chunk") ||
              error.message.includes("missing:") ||
              error.message.includes("Failed to fetch dynamically imported module"))))
    );

    this.setState({ error: error, errorInfo: errorInfo, isChunkError });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    sessionStorage.removeItem("chunk_has_reloaded");
    window.location.reload();
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bgcolor="#121212"
            p={3}
          >
            <Paper
              elevation={4}
              style={{
                padding: "32px",
                maxWidth: "540px",
                width: "100%",
                borderRadius: "16px",
                backgroundColor: "#1e1e1e",
                color: "#fff",
                textAlign: "center",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box mb={2}>
                <RefreshIcon style={{ fontSize: "56px", color: "#ffb74d" }} />
              </Box>
              <Typography
                variant="h5"
                gutterBottom
                style={{ fontWeight: "700", color: "#fff" }}
              >
                {t("new_version_available_title", "A new version is available")}
              </Typography>
              <Typography variant="body1" paragraph style={{ color: "#b0bec5", marginBottom: "24px" }}>
                {t(
                  "new_version_available_message",
                  "The application has been updated. Please refresh the page to get the latest features."
                )}
              </Typography>

              <Button
                variant="contained"
                onClick={this.handleReload}
                startIcon={<RefreshIcon />}
                style={{
                  backgroundColor: "#ffb74d",
                  color: "#000",
                  fontWeight: "bold",
                  padding: "10px 24px",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "15px",
                }}
              >
                {t("refresh_now", "Refresh now")}
              </Button>
            </Paper>
          </Box>
        );
      }

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="#f8d7da"
          p={3}
        >
          <Paper
            elevation={3}
            style={{
              padding: "30px",
              maxWidth: "800px",
              width: "100%",
              borderColor: "#f5c6cb",
              backgroundColor: "#fff",
            }}
          >
            <Typography
              variant="h4"
              color="error"
              gutterBottom
              style={{ fontWeight: "bold" }}
            >
              {t("error_boundary_title", "Something went wrong.")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t(
                "error_boundary_message",
                "The application encountered an unexpected error. Please report the details below to the administrator."
              )}
            </Typography>

            <Box
              bgcolor="#000"
              color="#fff"
              p={2}
              borderRadius={2}
              style={{
                overflowX: "auto",
                textAlign: "left",
                marginBottom: "20px",
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
              >
                {this.state.error && this.state.error.toString()}
                <br />
                <br />
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReload}
              style={{ marginTop: "20px" }}
            >
              {t("reload_application", "Reload Application")}
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
